import React, {useCallback, useEffect, useState, useContext} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {Button, Form, Input, Typography, Row, App, Modal} from 'antd';
import {
    UserOutlined,
    LockOutlined,
    EyeInvisibleOutlined,
    EyeOutlined,
    WarningOutlined, CheckCircleOutlined
} from '@ant-design/icons';
import './Login.css';
import {MainImage} from "../../components/image/MainImage";
import {themeLocations} from "../../theme/themeLocations";
import {getUsernameUsingSSOToken, signInUser, getUser, getValueFromCookieOnServer} from "../../api/security";
import MainLayout from "../../components/layout/MainLayout";
import {showApiError} from "../../utility/error/error-handler";
import {getDriverRouteById} from "../../components/routes/driverRoutes";
import {useTranslation} from "react-i18next";
import i18n from "i18next";
import {
    encryptTextValue,
    getSessionData,
    storeSessionData,
    getLocalData,
    storeLocalData,
} from "../../utility/global-util";
import {getSessionCookie, updateSSLCookie} from "../../utility/cookie-utility";
import {sendForgotPasswordRequest} from "../../api/user";
import HeaderBanner from "../../components/layout/HeaderBanner";
import CentralLoading from "../../components/layout/CentralLoading";
import useOnlineStatus from "../../utility/hooks/useOnlineStatus";
import GlobalContext from "../../GlobalContext";

const { Text} = Typography;


// The root login component.
// Checks if the user is done with authentication and redirects them.
// Otherwise, displays the login form.
const Login = () => {
    const globalData = useContext(GlobalContext);

    const { t } = useTranslation();

    const { isOnline } = useOnlineStatus();

    const { message, notification } = App.useApp();

    const navigate = useNavigate();

    const [searchParams] = useSearchParams();

    const [loginProgress, setLoginProgress] = useState(100);  //start 100 - button shows loading if less than 100
    const [forgotProgress, setForgotProgress] = useState(100);  //start 100 - button shows loading if less than 100

    const [ssoLoginId, setSsoLoginId] = useState(null);
    const [checkExisting, setCheckExisting] = useState(getSessionCookie() !== undefined && getSessionData('loginHash') !== '');
    const [existingValid, setExistingValid] = useState(false);
    
    const destination = globalData.requestedRoute ? globalData.requestedRoute : 'home';

    const [textVisible, setTextVisible] = useState(false);

    const suffixIcon = textVisible
        ? (<EyeInvisibleOutlined onClick={() => setTextVisible(false)} />)
        : (<EyeOutlined onClick={() => setTextVisible(true)} />);
    const fieldType = textVisible ? 'text' : 'password';

    // LOGIN
    const initialiseUser = (res) => {
        // Login User - set user info on session (only add non-sensitive info to the session or local storage)
        storeSessionData('userUsername', res.user.username);
        storeSessionData('userDisplayName', res.user.displayName);
        storeSessionData('userCustomers', res.customers);
        storeSessionData('selectedCustomer', res.selectedCustomer);
        storeSessionData('driverID', res.driverId);
        storeSessionData('contextRoot', res.contextRoot);
        storeSessionData('idleSeconds', res.idleSeconds);
        storeSessionData('loginTimestamp', new Date().getTime());
        storeSessionData('driverPermissions', res.driverPermissions);
        storeSessionData('loginHash', encryptTextValue(getSessionCookie()));

        // Change translation language now (after this it is detected from the session locale)
        storeSessionData('userLocale', res.user.locale);
        i18n.changeLanguage(res.user.locale.replace('_', '-'));
    }

    const onFinish = useCallback((values, isSSO = false) => {
        console.log('Logging in with user: ' + values.username);

        // ONLINE
        if(isOnline) {
            //form validation on form
            signInUser(values.username, values.password, setLoginProgress)
              .then(res => {

                // Check if user returned
                if (res && res.user) {
                    // Login and setup user
                    initialiseUser(res);
                    updateSSLCookie();
                    // Check for expired password
                    if (res.user.passwordChangeRequired) {
                        // Redirect to password change page if it has
                        storeSessionData('keepExpXXX', values.password);
                        navigate(getDriverRouteById('change-pwd') + '?mode=expired');
                    } else {
                        // Store login credentials for offline use
                        storeLocalData(encryptTextValue(values.username + ':' + values.password), res);

                        // Message and navigate to home page
                        message.success(t('message.successfully_logged_in'));
                        notification.destroy();
                        navigate(getDriverRouteById(destination));
                    }
                }
              })
              .catch(err => {
                  if (isSSO) {
                      console.error('Error logging in SSO (not showing user):');  //should not happen, if timing issue should log in again
                      console.error(err);
                  } else {
                    
                      const loginMessageOverrides = [
                          {status: 401, message: t('error.incorrect_credentials')},
                          {status: 403, message: t('error.user_403')},
                          {status: 406, message: t('error.user_406')},
                          {status: 412, message: t('error.version_mismatch')},
                          {status: 418, message: t('error.user_locked')},
                      ];
                      showApiError(t('title.login'), err, notification, loginMessageOverrides);  //standard messages handled, override or extra in loginMessageOverrides
                  }
              });
        }

        // OFFLINE
        else {
            if (!isSSO) { //SSO cant login offline
                let res = getLocalData(encryptTextValue(values.username + ':' + values.password));
                if (res) {
                    // Setup user
                    initialiseUser(res);

                    // Message and navigate to home page
                    message.success(t('message.successfully_logged_in'));
                    notification.destroy();
                    navigate(getDriverRouteById(destination));
                } else {
                    notification.open({
                        icon: <WarningOutlined style={{color: 'var(--dp-status-colour-warning)'}}/>,
                        message: t('title.login'),
                        description: t('error.incorrect_credentials'),
                        duration: null,
                    });
                }
            }
        }
    }, [notification, message, navigate, t, isOnline, destination]);

    const onFinishFailed = (errorInfo) => {
      console.log('Form Failed:', errorInfo);
    };

    // FORGOT PASSWORD
    const [isPwdRequestModalVisible, setIsPwdRequestModalVisible] = useState(false);

    const onFinishPwdRequest = (values) => {

        sendForgotPasswordRequest({username: values.pwd_username, emailAddress: values.pwd_email}, getDriverRouteById('set-pwd'), setForgotProgress)
            .then(() => {
                // no data returned, give success message and close popup
                notification.destroy();
                notification.open({
                    icon: <CheckCircleOutlined style={{color: 'var(--dp-status-colour-ok)'}}/>,
                    description: t('message.forgot_pwd_success', {0: values.pwd_email}),
                    duration: null,
                });
                setIsPwdRequestModalVisible(false);
            })
            .catch(err => {
                const loginMessageOverrides = [
                    { status: 400, message: t('message.forgot_pwd_mismatch')},
                    { status: 409, message: t('message.forgot_pwd_mismatch')},
                    { status: 418, message: t('error.user_locked')},
                ];
                showApiError(null, err, notification, loginMessageOverrides);  //standard messages handled, override or extra in loginMessageOverrides
            });
    };
    const onFinishPwdRequestFailed = (errorInfo) => {
        console.log('Form Failed:', errorInfo);
    };
    const onCancelPwdRequest = () => {
        setIsPwdRequestModalVisible(false);
        notification.destroy();
    };

    // LOAD
    useEffect(() => {
        // Check for SSO (for external SSO or open ID login - not LDAP) - must be online at the moment
        const ssoId = searchParams ? searchParams.get("sso_id") : null;
        setSsoLoginId(ssoId);
        if (ssoId) {
            // Store provider and destination on session for logout re-login
            storeSessionData('ssoProvider', searchParams ? searchParams.get("provider") : null);
            storeSessionData('ssoDestination', searchParams ? searchParams.get("destination") : null);
        } else {
            storeSessionData('ssoProvider', null);
            storeSessionData('ssoDestination', null);
        }
    }, [searchParams]);

    useEffect(() => {
        // External login
        if (ssoLoginId) {
            notification.destroy();  //close any notifications if came here after redirect

            // Get SSO token from cookie set by redirecting servlet
            const cookieName = 'fleetSSO' + ssoLoginId;
            getValueFromCookieOnServer(cookieName, null)
                .then(ssoToken => {
                    if (ssoToken) {
                        // Get user details to login with
                        getUsernameUsingSSOToken(ssoToken, null)
                            .then(res => {
                                if (res && res.username) {
                                    // Login
                                    onFinish(res, true);
                                } else {
                                    // No user found for token (user matching email checked in servlets)
                                    console.error('user not found for sso token passed in!');
                                    navigate(getDriverRouteById('sso-error'));
                                }
                            })
                            .catch(err => {
                                console.error('error logging in with SSO token!');
                                console.error(err);
                                navigate(getDriverRouteById('sso-error'));
                            });
                    }
                })
                .catch(err => {
                    console.error('error obtaining SSO token from cookie!');
                    console.error(err);
                    navigate(getDriverRouteById('sso-error'));
                });
        }
    }, [notification, navigate, onFinish, ssoLoginId]);

    useEffect(() => {
        if (ssoLoginId == null) {
            notification.destroy();  //close any notifications if came here after expiry
            setExistingValid(getSessionData('loginHash') !== null && getSessionData('loginHash') !== '');
            if (checkExisting) {
                getUser().then((res) => {
                    if (res) {
                        setExistingValid(true);
                        initialiseUser(res);
                        navigate(getDriverRouteById(destination));
                    }
                    setCheckExisting(false);
                })
                    .catch(err => {
                        console.error('error checking user session for existing user');
                        console.error(err);
                        setCheckExisting(false);
                    });
            }
        }
    }, [notification, navigate, ssoLoginId, checkExisting, setCheckExisting, setExistingValid, destination]);

    // PAGE - show underlying loading... message for SSO case or the login page
    return (
        ssoLoginId || checkExisting || existingValid
            ?
            <CentralLoading />
            :
            <MainLayout showHeader={false} showTabMenu={false} showSubHeader={true} startHigher="230px"
                        subHeaderContent={<HeaderBanner/>} subHeaderClass="dp-full-no-vehicle">
                <Row type="flex" align="middle" justify="center">
                    <div className="login-content">
                        <Row type="flex" justify="center" className="login-logo">
                            <MainImage imageSrc={themeLocations.MAIN_LOGO}/>
                        </Row>
                        <Row>
                            <Form
                                className="login-form"
                                name="login-dp"
                                initialValues={{
                                    remember: true,
                                }}
                                onFinish={onFinish}
                                onFinishFailed={onFinishFailed}
                                autoComplete="off"
                            >
                                <Form.Item
                                    name="username"
                                    rules={[
                                        {required: true, message: t('error.no_value_named', {0: t('field.username')})},
                                    ]}
                                >
                                    <Input
                                        type="text"
                                        autoComplete="off"
                                        prefix={<UserOutlined style={{color: 'rgb(131,135,137)'}}/>}
                                        placeholder={t('field.username')}
                                        maxLength={80}
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="password"
                                    rules={[
                                        {required: true, message: t('error.no_value_named', {0: t('field.password')})},
                                    ]}
                                    style={{marginTop: '30px'}}
                                >
                                    <Input
                                        type={fieldType}
                                        prefix={<LockOutlined style={{color: 'rgb(131,135,137)'}}/>}
                                        placeholder={t('field.password')}
                                        maxLength={80}
                                        suffix={suffixIcon}
                                    />
                                </Form.Item>

                                <Form.Item>
                                    <Button type="primary" htmlType="submit" block loading={loginProgress < 100}>
                                        {t('action.submit')}
                                    </Button>
                                </Form.Item>

                                {isOnline
                                    ? <Form.Item
                                        name="forgot-password"
                                    >
                                        <Row justify="end" className="login-forgot-password">
                                            <Button type="link" onClick={() => setIsPwdRequestModalVisible(true)}>
                                                <Text>{t('action.forgot_password')}</Text>
                                            </Button>
                                        </Row>
                                    </Form.Item>
                                    : <></>}
                            </Form>
                        </Row>
                    </div>
                    <Modal className="std-portal-popup"
                           title={t('action.forgot_password')}
                           open={isPwdRequestModalVisible}
                           closable={true}
                           maskClosable={false}
                           onCancel={onCancelPwdRequest}
                           keyboard={false}
                           footer={[
                               <Button onClick={onCancelPwdRequest}>
                                   {t('action.cancel')}
                               </Button>,
                               <Button type="primary" form="pwd-req-form" key="submit" htmlType="submit"
                                       loading={forgotProgress < 100}>
                                   {t('action.submit')}
                               </Button>,
                           ]}>
                        <div>
                            <Form
                                name="pwd-req-form"
                                onFinish={onFinishPwdRequest}
                                onFinishFailed={onFinishPwdRequestFailed}
                                autoComplete="off"
                                layout="vertical"
                                initialValues={{pwd_username: null, pwd_email: null}}
                            >
                                <Form.Item
                                    name="pwd_username"
                                    label={t('field.username')}
                                    rules={[
                                        {required: true, message: t('error.no_value_named', {0: t('field.username')})},
                                    ]}
                                >
                                    <Input
                                        type="text"
                                        autoComplete="off"
                                        allowClear={true}
                                        placeholder={t('action.enter_value')}
                                        maxLength={80}
                                    />
                                </Form.Item>
                                <Form.Item
                                    name="pwd_email"
                                    label={t('field.email')}
                                    rules={[
                                        {required: true, message: t('error.no_value_named', {0: t('field.email')})},
                                        {type: 'email', message: t('error.invalid_email')},
                                    ]}
                                >
                                    <Input
                                        type="email"
                                        placeholder={t('action.enter_value')}
                                        maxLength={254}
                                        allowClear={true}
                                    />
                                </Form.Item>
                            </Form>
                        </div>
                    </Modal>
                </Row>
            </MainLayout>
    );
}

export default Login;
